import React, { useMemo, useRef, useState, useEffect } from "react";
import { ArrowLeft, Upload } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from '../config'

export default function AddNewJob() {
  const navigate = useNavigate();
  const location = useLocation();
  const jobToEdit = location.state?.job || null;
const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    title: "",
    category: "Contract",
    level: "Junior",
    email: "",
    location: "",
    description: "",
  });
  const [image, setImage] = useState(null);
  const [errors, setErrors] = useState({});
  const fileRef = useRef(null);

  const levels = ["Junior", "Mid", "Senior", "Lead", "Director"];
  const categories = [
    "Contract",
    "Monthly",
    "Part-time",
    "Full-time",
    "Internship",
    "Freelance",
    "Temporary",
  ];

  // Populate form if editing
  useEffect(() => {
    if (jobToEdit) {
      setForm({
        name: jobToEdit.name || "",
        title: jobToEdit.title || "",
        category: jobToEdit.category || "Contract",
        level: jobToEdit.level || "Junior",
        email: jobToEdit.email || "",
        location: jobToEdit.location || "",
        description: jobToEdit.description || "",
      });
    }
  }, [jobToEdit]);

  const previewUrl = useMemo(
    () =>
      image
        ? URL.createObjectURL(image)
        : jobToEdit?.image
        ? `${config.BASE_URL}uploads/${jobToEdit.image}`
        : null,
    [image, jobToEdit]
  );

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    let newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.title.trim()) newErrors.title = "Job title is required";
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!form.location.trim()) newErrors.location = "Location is required";
    if (!form.description.trim() || form.description.length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }
       // Image validation
    if (!image && !jobToEdit?.image) {
      newErrors.image = "Image is required";
    } else if (image) {
      if (!["image/jpeg", "image/png"].includes(image.type)) {
        newErrors.image = "Only JPG or PNG allowed";
      }
      if (image.size > 10 * 1024 * 1024) {
        newErrors.image = "File size must be less than 10MB";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onClear = () => {
    setForm({
      name: "",
      title: "",
      category: "Contract",
      level: "Junior",
      email: "",
      location: "",
      description: "",
    });
    setImage(null);
    setErrors({});
    if (fileRef.current) fileRef.current.value = "";
  };

const onSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;

  setLoading(true); // Start loading

  const payload = new FormData();
  Object.entries(form).forEach(([k, v]) => payload.append(k, v));
  if (image) payload.append("image", image);

  try {
    if (jobToEdit) {
      await axios.put(`${config.BASE_URL}/jobs/${jobToEdit.id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      //alert("Job updated! ✅");
    } else {
      await axios.post(`${config.BASE_URL}/jobs`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
     // alert("Job added! ✅");
    }
    navigate("/dashboard/jobs");
  } catch (err) {
    console.error(err);
    alert("Something went wrong");
  } finally {
    setLoading(false); // Stop loading
  }
};


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="mx-auto w-full max-w-5xl px-6 pt-6">
        <div className="flex items-center gap-3 text-gray-900">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white shadow hover:bg-gray-100"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-3xl font-bold">Jobs</h1>
        </div>
        <div className="mt-6 rounded-md border border-gray-900 bg-gray-900 px-5 py-3 text-white shadow-sm">
          <span className="font-semibold">
            {jobToEdit ? "Edit Job" : "Add New Job"}
          </span>
        </div>
      </header>

      {/* Card */}
      <main className="mx-auto my-8 w-full max-w-5xl px-6 pb-20">
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <div className="relative h-36 w-full rounded-lg bg-gray-300">
            <h2 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-semibold text-lg text-center text-white">
              Job Info
            </h2>

            {/* Upload / Preview */}
            <div className="absolute left-1/2 -translate-x-1/2 -bottom-20 w-40 h-40">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="h-40 w-40 rounded-2xl object-cover border shadow-lg cursor-pointer"
                  onClick={() => fileRef.current?.click()}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="group flex h-40 w-40 flex-col items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300"
                >
                  <Upload className="h-6 w-6" />
                  <span className="text-sm">Upload Image</span>
                </button>
              )}

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setImage(e.target.files?.[0] ?? null)}
              />
              {errors.image && (
                <p className="mt-2 text-center text-xs text-red-500">
                  {errors.image}
                </p>
              )}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="mt-24 space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Enter name"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 focus:border-gray-900 focus:outline-none"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Job Title
                </label>
                <input
                  name="title"
                  value={form.title}
                  onChange={onChange}
                  placeholder="Enter title"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 focus:border-gray-900 focus:outline-none"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                )}
              </div>
            </div>

            {/* Category & Level */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="relative">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  name="category"
                  value={form.category}
                  onChange={onChange}
                  className="w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 py-2 pr-10 text-gray-900 focus:border-gray-900 focus:outline-none"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              <div className="relative">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Level
                </label>
                <select
                  name="level"
                  value={form.level}
                  onChange={onChange}
                  className="w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 py-2 pr-10 text-gray-900 focus:border-gray-900 focus:outline-none"
                >
                  {levels.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Email & Location */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="Enter email"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 focus:border-gray-900 focus:outline-none"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Location
                </label>
                <input
                  name="location"
                  value={form.location}
                  onChange={onChange}
                  placeholder="Enter location"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 focus:border-gray-900 focus:outline-none"
                />
                {errors.location && (
                  <p className="mt-1 text-sm text-red-500">{errors.location}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                rows={5}
                value={form.description}
                onChange={onChange}
                placeholder="Write job description…"
                className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 focus:border-gray-900 focus:outline-none"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description}</p>
              )}
            </div>

            {/* Actions */}
            <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClear}
                className="inline-flex w-full items-center justify-center rounded-lg bg-gray-200 px-6 py-2 font-medium text-gray-700 hover:bg-gray-300 sm:w-40"
              >
                Clear
              </button>
              <button
                type="submit"
                className="inline-flex w-full items-center justify-center rounded-lg bg-gray-900 px-6 py-2 font-semibold text-white hover:bg-black sm:w-40"
              >
                {jobToEdit ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </div>
      </main>

      {loading && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="rounded-lg bg-white px-6 py-4 text-gray-900 shadow-lg">
     Uploading...
    </div>
  </div>
)}

    </div>
  );
}
